import { useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';

import { inRn } from 'lib/helpers/utility';

import getTableParams from 'components/shared/AgTablePage/lib/getParams';

export const useTableSearchParams = (paramPrefix = '', name, isInfinite) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { sortField, page, view, sort } = getTableParams(searchParams, paramPrefix || name);

  const getParamKey = useCallback(
    (key) => (paramPrefix ? `${paramPrefix}_${key}` : key),
    [paramPrefix]
  );

  const onPageChange = useCallback(
    (nextPage) => {
      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        const safePage = inRn(nextPage, 1);
        newParams.set(getParamKey('page'), String(safePage));
        return newParams;
      });
    },
    [getParamKey, setSearchParams]
  );

  const resetPage = useCallback(async () => {
    if (isInfinite) return;

    await setSearchParams((prevParams) => {
      if ((prevParams.get(getParamKey('page')) ?? '1') === '1') return prevParams;

      const newParams = new URLSearchParams(prevParams);
      newParams.set(getParamKey('page'), '1');
      return newParams;
    });
  }, [getParamKey, setSearchParams, isInfinite]);

  const resetSortAndPage = useCallback(async () => {
    await setSearchParams((prevParams) => {
      const newParams = new URLSearchParams(prevParams);
      newParams.delete(getParamKey('sort'));
      newParams.delete(getParamKey('order'));

      if (!isInfinite) newParams.set(getParamKey('page'), '1');
      return newParams;
    });
  }, [getParamKey, setSearchParams, isInfinite]);

  const onSortChange = useCallback(
    (sortedColumns) => {
      const [sortedColumn] = sortedColumns || [];

      setSearchParams((prevParams) => {
        const newParams = new URLSearchParams(prevParams);
        const prevSort = prevParams.get(getParamKey('sort')) || '';
        const prevOrder = prevParams.get(getParamKey('order')) || '';

        let nextSort = '';
        let nextOrder = '';
        if (
          sortedColumn &&
          sortedColumn.colId &&
          (sortedColumn.sort === 'asc' || sortedColumn.sort === 'desc')
        ) {
          nextSort = sortedColumn.colId;
          nextOrder = sortedColumn.sort;
        }

        const sortChanged = nextSort !== prevSort || nextOrder !== prevOrder;
        if (!sortChanged) return prevParams;

        if (nextSort) {
          newParams.set(getParamKey('sort'), nextSort);
          newParams.set(getParamKey('order'), nextOrder);
        } else {
          newParams.delete(getParamKey('sort'));
          newParams.delete(getParamKey('order'));
        }

        if (!isInfinite) newParams.set(getParamKey('page'), '1');
        return newParams;
      });
    },
    [getParamKey, setSearchParams, isInfinite]
  );

  return { onPageChange, resetPage, resetSortAndPage, onSortChange, view, page, sortField, sort };
};
